/**
 * Sessão recusada pelo servidor (HTTP 401).
 *
 * POR QUÊ este arquivo existe: até agora NINGUÉM tratava 401 no cliente. O
 * adapter devolvia `{ data: null, error }`, os hooks liam isso como "sem
 * dados", e o quadro desenhava **zeros**. O gerente via "Atrasados 0, Em
 * Produção 0" numa tela perfeitamente funcional — que é pior do que um erro,
 * porque parece uma boa notícia. Aconteceu duas vezes em produção.
 *
 * A regra: token recusado = sessão morta. Limpa e manda para o login dizendo
 * o que houve, em vez de fingir que a fábrica está parada.
 */

const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';
/** Marca que a saída foi por sessão vencida, para o login explicar. */
const FLAG_KEY = 'sessao_expirada';

/** Rotas onde 401 é resposta NORMAL e não pode virar redirecionamento. */
function ehRotaDeAutenticacao(path: string): boolean {
  // Senha errada no login devolve 401. Redirecionar aqui criaria um laço:
  // /auth → login falha → redireciona para /auth → …
  return /\/api\/auth\//.test(path);
}

// Várias queries falham juntas na mesma carga do quadro. Sem esta trava, cada
// uma dispararia seu próprio redirecionamento.
let jaRedirecionando = false;

/**
 * Trata um 401. Devolve true se assumiu o controle (sessão encerrada), para
 * quem chamou saber que não vale a pena propagar o erro na tela.
 */
export function handleUnauthorized(path: string): boolean {
  if (ehRotaDeAutenticacao(path)) return false;
  if (jaRedirecionando) return true;
  jaRedirecionando = true;

  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  } catch {
    /* localStorage indisponível — segue para o login mesmo assim */
  }

  // Navegação DURA, não router: a sessão morreu e todo o cache do React Query
  // ficou com dados de um usuário que não existe mais. Recarregar é o que
  // garante que nada sobreviva.
  marcarSessaoExpirada();
  try {
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/auth')) {
      window.location.replace('/auth?sessao=expirada');
    }
  } catch {
    /* ambiente sem window (teste) — nada a fazer */
  }
  return true;
}

/**
 * Marca "a sessão venceu" sem redirecionar.
 *
 * Existe para o caminho da CARGA da página: o `getSession()` do adapter de auth
 * já detecta o token recusado e limpa tudo, e o ProtectedRoute leva para o
 * login sozinho — mas SEM dizer por quê, e o usuário conclui que o sistema
 * "deslogou do nada". Aqui só se deixa o bilhete; quem redireciona continua
 * sendo o roteador.
 */
export function marcarSessaoExpirada(): void {
  try {
    sessionStorage.setItem(FLAG_KEY, '1');
  } catch {
    /* sem sessionStorage — o login apenas não explica o motivo */
  }
}

/** Lê e CONSOME o bilhete: o aviso aparece uma vez, não em todo login. */
export function consumirSessaoExpirada(): boolean {
  try {
    if (sessionStorage.getItem(FLAG_KEY) !== '1') return false;
    sessionStorage.removeItem(FLAG_KEY);
    return true;
  } catch {
    return false;
  }
}

